import { useState, useEffect, useRef } from "react";

interface Props { theme: "dark" | "light"; lang: "fr" | "en" | "ar"; }

const tabs = {
  fr: [
    { label: "📍 Trouvez une station", key: "map" },
    { label: "⛽ Vérifiez le carburant", key: "detail" },
    { label: "🔔 Restez informé", key: "alerts" },
  ],
  en: [
    { label: "📍 Find a station", key: "map" },
    { label: "⛽ Check fuel", key: "detail" },
    { label: "🔔 Stay informed", key: "alerts" },
  ],
  ar: [
    { label: "📍 ابحث عن محطة", key: "map" },
    { label: "⛽ تحقق من الوقود", key: "detail" },
    { label: "🔔 ابقَ على اطلاع", key: "alerts" },
  ],
};

const descriptions = {
  fr: {
    map: "La carte interactive affiche toutes les stations en temps réel. Les marqueurs colorés indiquent instantanément la disponibilité du carburant.",
    detail: "Consultez les détails complets d'une station : types de carburant, prix, horaires, et statut mis à jour par la communauté.",
    alerts: "Activez les alertes pour être notifié dès qu'une station proche de vous a du carburant disponible.",
  },
  en: {
    map: "The interactive map shows all stations in real time. Colored markers instantly indicate fuel availability.",
    detail: "View full station details: fuel types, prices, hours, and community-updated status.",
    alerts: "Enable alerts to be notified as soon as a nearby station has fuel available.",
  },
  ar: {
    map: "تعرض الخريطة التفاعلية جميع المحطات في الوقت الفعلي. المؤشرات الملونة تشير فوراً إلى توفر الوقود.",
    detail: "اعرض تفاصيل المحطة الكاملة: أنواع الوقود والأسعار والساعات والحالة المحدّثة من المجتمع.",
    alerts: "فعّل التنبيهات لتُخطَر فور توفر وقود في محطة قريبة منك.",
  },
};

function DemoTabBar({ active = 0 }: { active?: number }) {
  const tabs = [
    { icon: "ri-map-2-fill", label: "Map" },
    { icon: "ri-list-unordered", label: "Regions" },
    { icon: "ri-notification-3-line", label: "Alerts" },
    { icon: "ri-user-line", label: "Profile" },
  ];
  return (
    <div style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(20px)", borderTop: "1px solid rgba(255,255,255,0.10)", height: 82, flexShrink: 0, display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-around", flex: 1, paddingTop: 8 }}>
        {tabs.map((tab, i) => (
          <div key={tab.label} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
            <i className={tab.icon} style={{ fontSize: 18, color: i === active ? "#007AFF" : "rgba(255,255,255,0.40)" }} />
            <span style={{ fontSize: 8, fontWeight: i === active ? 600 : 400, color: i === active ? "#007AFF" : "rgba(255,255,255,0.40)", fontFamily: "-apple-system, system-ui" }}>{tab.label}</span>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", justifyContent: "center", paddingBottom: 8 }}>
        <div style={{ width: 120, height: 4, background: "rgba(255,255,255,0.28)", borderRadius: 2 }} />
      </div>
    </div>
  );
}

function MapScreen({ isDark }: { isDark: boolean }) {
  return (
    <div className="w-full h-full flex flex-col" style={{ background: "#0A0A10" }}>
      <div className="flex justify-between items-center px-5 pb-2" style={{ paddingTop: 54 }}>
        <span className="text-white text-xs font-semibold">04:37</span>
        <div className="flex items-center gap-1"><i className="ri-wifi-fill text-white text-xs" /><i className="ri-battery-fill text-white text-xs" /></div>
      </div>
      <div className="px-3 pb-2">
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl" style={{ background: "rgba(255,255,255,0.10)", border: "1px solid rgba(255,255,255,0.08)" }}>
          <i className="ri-search-line text-white/50 text-sm" />
          <span className="text-white/40 text-xs">Search fuel stations...</span>
        </div>
      </div>
      <div className="flex gap-2 px-3 pb-3 overflow-hidden">
        {["● Your location", "Hodh ech Chargui", "Trarza"].map((c, i) => (
          <div key={c} className="px-2.5 py-1 rounded-full text-[10px] whitespace-nowrap" style={{ background: i === 0 ? "rgba(0,122,255,0.20)" : "rgba(255,255,255,0.08)", border: i === 0 ? "1px solid rgba(0,122,255,0.40)" : "1px solid rgba(255,255,255,0.10)", color: i === 0 ? "#007AFF" : "rgba(255,255,255,0.60)" }}>{c}</div>
        ))}
      </div>
      <div className="flex-1 relative" style={{ background: "#0A0A10" }}>
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 270 320" preserveAspectRatio="xMidYMid slice">
          <g stroke="rgba(255,255,255,0.09)" strokeWidth="1.2" fill="none">
            {[55,110,165,220].map(x => <line key={x} x1={x} y1="0" x2={x+8} y2="320" />)}
            {[55,110,165,220,275].map(y => <line key={y} x1="0" y1={y} x2="270" y2={y+6} />)}
          </g>
        </svg>
        <div className="absolute" style={{ left: "50%", top: "50%", transform: "translate(-50%,-50%)" }}>
          <span className="text-white/15 text-sm font-bold tracking-widest">Nouakchott</span>
        </div>
        <div className="absolute" style={{ left: "42%", top: "35%" }}>
          <div className="relative w-5 h-5 flex items-center justify-center">
            <div className="absolute w-5 h-5 rounded-full bg-[#007AFF]/30 animate-ping" />
            <div className="w-3 h-3 rounded-full bg-[#007AFF] border-2 border-white" />
          </div>
        </div>
        {[{ l: "55%", t: "55%", c: "#FF3B30" }, { l: "35%", t: "65%", c: "#34C759" }, { l: "62%", t: "40%", c: "#34C759" }, { l: "28%", t: "45%", c: "#FF9500" }].map((m, i) => (
          <div key={i} className="absolute w-4 h-4 rounded-full border-2 border-white/80" style={{ left: m.l, top: m.t, background: m.c }} />
        ))}
      </div>
      <DemoTabBar active={0} />
    </div>
  );
}

function DetailScreen() {
  return (
    <div className="w-full h-full flex flex-col overflow-hidden" style={{ background: "#0A0A10" }}>
      <div className="relative h-40 flex-shrink-0">
        <img src="https://readdy.ai/api/search-image?query=modern%20fuel%20gas%20station%20at%20night%20illuminated%20canopy%20dark%20sky%20mauritania%20africa%20cinematic%20dramatic%20lighting%20clean%20minimal%20background%20urban%20street&width=280&height=160&seq=demo1&orientation=landscape" alt="station" className="w-full h-full object-cover object-top" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom,rgba(0,0,0,0.2),rgba(0,0,0,0.75))" }} />
        <div className="absolute top-10 left-3 w-8 h-8 rounded-full flex items-center justify-center" style={{ background: "rgba(0,0,0,0.50)", backdropFilter: "blur(10px)", border: "1px solid rgba(255,255,255,0.15)" }}>
          <i className="ri-arrow-left-s-line text-white text-base" />
        </div>
        <div className="absolute bottom-3 left-3 flex items-center gap-1 px-2 py-1 rounded-full" style={{ background: "rgba(0,0,0,0.60)", backdropFilter: "blur(10px)", border: "1px solid rgba(255,255,255,0.15)" }}>
          <i className="ri-star-fill text-[#FF9500] text-xs" /><span className="text-white text-xs font-semibold">4.8</span>
        </div>
      </div>
      <div className="flex-1 overflow-hidden px-3 py-3 flex flex-col gap-2">
        <div><h3 className="text-white font-bold text-base">BPM Station</h3><p className="text-white/50 text-xs mt-0.5">74e Carffor jambor · Nouakchott</p></div>
        <div className="grid grid-cols-3 gap-1.5">
          {[{ icon: "ri-shopping-cart-line", v: "24/7", l: "Services" }, { icon: "ri-time-line", v: "5 min", l: "Attente" }, { icon: "ri-map-pin-line", v: "6.6 km", l: "Distance" }].map(s => (
            <div key={s.l} className="flex flex-col items-center gap-1 py-2 rounded-xl" style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.08)" }}>
              <i className={`${s.icon} text-white/60 text-sm`} /><span className="text-white text-xs font-semibold">{s.v}</span><span className="text-white/40 text-[9px]">{s.l}</span>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl" style={{ background: "rgba(52,199,89,0.12)", border: "1px solid rgba(52,199,89,0.25)" }}>
          <div className="w-2 h-2 rounded-full bg-[#34C759]" /><span className="text-[#34C759] text-xs font-semibold">Ouvert 24/7</span>
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          {[{ type: "Essence", price: "50.13", status: "Disponible", color: "#007AFF" }, { type: "Diesel", price: "50.12", status: "Stock faible", color: "#FF9500" }].map(f => (
            <div key={f.type} className="p-2.5 rounded-xl" style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.08)" }}>
              <div className="flex items-center gap-1 mb-1"><div className="w-1.5 h-1.5 rounded-full" style={{ background: f.color }} /><span className="text-[9px] font-semibold" style={{ color: f.color }}>{f.status}</span></div>
              <p className="text-white text-xs font-semibold">⛽ {f.type}</p><p className="text-white/60 text-[10px]">{f.price} MRU/L</p>
            </div>
          ))}
        </div>
        <button className="w-full py-2.5 rounded-full bg-[#007AFF] text-white text-xs font-semibold">Mettre à jour</button>
      </div>
      <DemoTabBar active={0} />
    </div>
  );
}

function AlertsScreen() {
  const alerts = [
    { icon: "ri-gas-station-line", color: "#34C759", bg: "rgba(52,199,89,0.12)", border: "rgba(52,199,89,0.25)", title: "Essence disponible", sub: "Station Total · Tevragh Zeina", time: "Il y a 2 min" },
    { icon: "ri-notification-3-line", color: "#007AFF", bg: "rgba(0,122,255,0.12)", border: "rgba(0,122,255,0.25)", title: "Nouvelle station ajoutée", sub: "Shell · Ksar · 1.2 km", time: "Il y a 8 min" },
    { icon: "ri-alert-line", color: "#FF9500", bg: "rgba(255,149,0,0.12)", border: "rgba(255,149,0,0.25)", title: "Stock faible signalé", sub: "BPM Station · Sebkha", time: "Il y a 15 min" },
    { icon: "ri-close-circle-line", color: "#FF3B30", bg: "rgba(255,59,48,0.12)", border: "rgba(255,59,48,0.25)", title: "Épuisé", sub: "Station Agip · El Mina", time: "Il y a 22 min" },
    { icon: "ri-gas-station-line", color: "#34C759", bg: "rgba(52,199,89,0.12)", border: "rgba(52,199,89,0.25)", title: "Diesel disponible", sub: "Pétromin · Dar Naim", time: "Il y a 35 min" },
  ];
  return (
    <div className="w-full h-full flex flex-col" style={{ background: "#0A0A10" }}>
      <div className="flex justify-between items-center px-5 pt-12 pb-3">
        <span className="text-white text-xs font-semibold">04:37</span>
        <div className="flex items-center gap-1"><i className="ri-wifi-fill text-white text-xs" /><i className="ri-battery-fill text-white text-xs" /></div>
      </div>
      <div className="px-4 pb-3 flex items-center justify-between">
        <h3 className="text-white font-bold text-sm">Alertes</h3>
        <span className="text-[#007AFF] text-xs">Tout effacer</span>
      </div>
      <div className="flex-1 overflow-y-auto px-3 flex flex-col gap-2">
        {alerts.map((a, i) => (
          <div key={i} className="flex items-start gap-3 p-3 rounded-xl" style={{ background: a.bg, border: `1px solid ${a.border}` }}>
            <div className="w-8 h-8 flex items-center justify-center rounded-xl flex-shrink-0" style={{ background: "rgba(0,0,0,0.30)" }}>
              <i className={`${a.icon} text-sm`} style={{ color: a.color }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-xs font-semibold leading-tight">{a.title}</p>
              <p className="text-white/50 text-[10px] mt-0.5 truncate">{a.sub}</p>
            </div>
            <span className="text-white/30 text-[9px] whitespace-nowrap flex-shrink-0">{a.time}</span>
          </div>
        ))}
      </div>
      <DemoTabBar active={2} />
    </div>
  );
}

export default function DemoSection({ theme, lang }: Props) {
  const isDark = theme === "dark";
  const isRtl = lang === "ar";
  const [activeTab, setActiveTab] = useState(0);
  const [fading, setFading] = useState(false);
  const [visibleTab, setVisibleTab] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);
  const textPrimary = isDark ? "text-white" : "text-gray-900";
  const textMuted = isDark ? "text-white/60" : "text-gray-500";
  const cardBg = isDark ? "bg-[rgba(28,28,30,0.85)] border-white/10" : "bg-white/85 border-black/10";
  const t = tabs[lang];
  const desc = descriptions[lang];

  const switchTab = (i: number) => {
    if (i === activeTab) return;
    setFading(true);
    setTimeout(() => { setVisibleTab(i); setActiveTab(i); setFading(false); }, 350);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const el = entry.target as HTMLElement;
          el.style.opacity = "1"; el.style.transform = "translateY(0)";
        }
      });
    }, { threshold: 0.1 });
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const descKey = t[activeTab].key as "map" | "detail" | "alerts";

  return (
    <section className="py-28 relative overflow-hidden" style={{ background: isDark ? "#050508" : "#EAEAF0" }} dir={isRtl ? "rtl" : "ltr"}>
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 50% 50%, rgba(0,122,255,0.06) 0%, transparent 65%)" }} />
      <div ref={sectionRef} className="max-w-[1280px] mx-auto px-6 md:px-10" style={{ opacity: 0, transform: "translateY(30px)", transition: "opacity 0.7s ease, transform 0.7s ease" }}>
        <div className="text-center mb-14">
          <p className="text-[#007AFF] text-xs font-semibold tracking-[0.15em] uppercase mb-4">{lang === "fr" ? "DÉMO INTERACTIVE" : lang === "en" ? "INTERACTIVE DEMO" : "عرض تفاعلي"}</p>
          <h2 className={`text-4xl md:text-5xl font-bold tracking-tight mb-5 ${textPrimary}`} style={{ fontFamily: "-apple-system,'SF Pro Display',system-ui,sans-serif" }}>
            {lang === "fr" ? "Voyez l'application en action" : lang === "en" ? "See the app in action" : "شاهد التطبيق في العمل"}
          </h2>
        </div>

        <div className={`flex flex-col lg:flex-row items-center gap-12 ${isRtl ? "lg:flex-row-reverse" : ""}`}>
          {/* Phone */}
          <div className="flex-shrink-0 flex items-center justify-center">
            <div
              style={{
                width: 270,
                height: 585,
                background: "#000000",
                borderRadius: 48,
                padding: 3,
                boxShadow: "0 40px 80px rgba(0,122,255,0.15), 0 20px 40px rgba(0,0,0,0.5), inset 0 0 0 1px rgba(255,255,255,0.08)",
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: 45,
                  overflow: "hidden",
                  position: "relative",
                  background: "#0A0A10",
                }}
              >
                {/* Dynamic Island */}
                <div style={{ position: "absolute", top: 12, left: "50%", transform: "translateX(-50%)", width: 110, height: 32, background: "#000000", borderRadius: 18, zIndex: 30 }} />
                <div style={{ position: "absolute", inset: 0, opacity: fading ? 0 : 1, transition: "opacity 0.35s ease" }}>
                  {visibleTab === 0 && <MapScreen isDark={isDark} />}
                  {visibleTab === 1 && <DetailScreen />}
                  {visibleTab === 2 && <AlertsScreen />}
                </div>
              </div>
            </div>
          </div>

          {/* Right: tabs + description */}
          <div className="flex-1 flex flex-col gap-4 max-w-[520px]">
            {t.map((tab, i) => (
              <button
                key={tab.key}
                onClick={() => switchTab(i)}
                className={`w-full text-left p-5 rounded-2xl backdrop-blur-[20px] border transition-all duration-250 cursor-pointer ${activeTab === i ? "border-[#007AFF]/50 bg-[rgba(0,122,255,0.10)]" : cardBg}`}
              >
                <div className="flex items-center justify-between">
                  <span className={`text-base font-semibold ${activeTab === i ? "text-[#007AFF]" : textPrimary}`} style={{ fontFamily: "-apple-system,'SF Pro Display',system-ui,sans-serif" }}>{tab.label}</span>
                  <i className={`ri-arrow-right-s-line text-lg transition-transform duration-200 ${activeTab === i ? "text-[#007AFF] rotate-90" : textMuted}`} />
                </div>
                {activeTab === i && (
                  <p className={`text-sm leading-relaxed mt-2 ${textMuted}`}>{desc[descKey]}</p>
                )}
              </button>
            ))}

            {/* Status legend */}
            <div className={`p-5 rounded-2xl backdrop-blur-[20px] border ${cardBg}`}>
              <p className={`text-xs font-semibold uppercase tracking-wider mb-3 ${textMuted}`}>{lang === "fr" ? "Légende des statuts" : lang === "en" ? "Status legend" : "دليل الحالات"}</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { dot: "#007AFF", label: lang === "fr" ? "Disponible" : lang === "en" ? "Available" : "متاح" },
                  { dot: "#FF9500", label: lang === "fr" ? "Stock faible" : lang === "en" ? "Low stock" : "مخزون منخفض" },
                  { dot: "#FF3B30", label: lang === "fr" ? "Épuisé" : lang === "en" ? "Unavailable" : "نفد" },
                  { dot: "#34C759", label: lang === "fr" ? "Ouvert" : lang === "en" ? "Open" : "مفتوح" },
                ].map(s => (
                  <div key={s.label} className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: s.dot }} />
                    <span className={`text-xs ${textMuted}`}>{s.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
